import _regeneratorRuntime from "/home/circleci/project/workspaces/frontend/node_modules/babel-preset-react-app/node_modules/@babel/runtime/regenerator";
import _asyncToGenerator from "/home/circleci/project/workspaces/frontend/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import { CodedError, UnavailabilityError } from 'expo-modules-core';
import ExpoFontLoader from './ExpoFontLoader';
import { FontDisplay } from './Font.types';
import { getAssetForSource, loadSingleFontAsync, fontFamilyNeedsScoping, getNativeFontName } from './FontLoader';
var loaded = {};
var loadPromises = {}; // @needsAudit
// note(brentvatne): at some point we may want to warn if this is called outside of a managed app.

/**
 * Used to transform font family names to the scoped name. This does not need to
 * be called in standalone or bare apps but it will return unscoped font family
 * names if it is called in those contexts.
 *
 * @param fontFamily Name of font to process.
 * @returns Returns a name processed for use with the [current workflow](https://docs.expo.io/introduction/managed-vs-bare/).
 */

export function processFontFamily(fontFamily) {
  if (!fontFamily || !fontFamilyNeedsScoping(fontFamily)) {
    return fontFamily;
  }

  if (!isLoaded(fontFamily)) {
    if (__DEV__) {
      if (isLoading(fontFamily)) {
        console.error("You started loading the font \"".concat(fontFamily, "\", but used it before it finished loading. You need to wait for Font.loadAsync to complete before using the font."));
      } else {
        console.error("fontFamily \"".concat(fontFamily, "\" is not a system font and has not been loaded through Font.loadAsync.\n\n- If you intended to use a system font, make sure you typed the name correctly and that it is supported by your device operating system.\n\n- If this is a custom font, be sure to load it with Font.loadAsync."));
      }
    }

    return 'System';
  }

  return "ExpoFont-".concat(getNativeFontName(fontFamily));
} // @needsAudit

/**
 * Synchronously detect if the font for `fontFamily` has finished loading.
 *
 * @param fontFamily The name used to load the `FontResource`.
 * @return Returns `true` if the font has fully loaded.
 */

export function isLoaded(fontFamily) {
  return fontFamily in loaded;
} // @needsAudit

/**
 * Synchronously detect if the font for `fontFamily` is still being loaded.
 *
 * @param fontFamily The name used to load the `FontResource`.
 * @returns Returns `true` if the font is still loading.
 */

export function isLoading(fontFamily) {
  return fontFamily in loadPromises;
} // @needsAudit

/**
 * Highly efficient method for loading fonts from static or remote resources which can then be used
 * with the platform's native text elements. In the browser this generates a `@font-face` block in
 * a shared style sheet for fonts. No CSS is needed to use this method.
 *
 * @param fontFamilyOrFontMap string or map of values that can be used as the [`fontFamily`](https://reactnative.dev/docs/text#style)
 * style prop with React Native Text elements.
 * @param source the font asset that should be loaded into the `fontFamily` namespace.
 *
 * @return Returns a promise that fulfils when the font has loaded. Often you may want to wrap the
 * method in a `try/catch/finally` to ensure the app continues if the font fails to load.
 */

export function loadAsync(_x, _x2) {
  return _loadAsync.apply(this, arguments);
}

function _loadAsync() {
  _loadAsync = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(fontFamilyOrFontMap, source) {
    var fontMap, names;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (!(typeof fontFamilyOrFontMap === 'object')) {
              _context.next = 8;
              break;
            }

            if (!source) {
              _context.next = 3;
              break;
            }

            throw new CodedError("ERR_FONT_API", "No fontFamily can be used for the provided source: ".concat(source, ". The second argument of `loadAsync()` can only be used with a `string` value as the first argument."));

          case 3:
            fontMap = fontFamilyOrFontMap;
            names = Object.keys(fontMap);
            _context.next = 7;
            return Promise.all(names.map(function (name) {
              return loadFontInNamespaceAsync(name, fontMap[name]);
            }));

          case 7:
            return _context.abrupt("return");

          case 8:
            _context.next = 10;
            return loadFontInNamespaceAsync(fontFamilyOrFontMap, source);

          case 10:
            return _context.abrupt("return", _context.sent);

          case 11:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _loadAsync.apply(this, arguments);
}

function loadFontInNamespaceAsync(_x3, _x4) {
  return _loadFontInNamespaceAsync.apply(this, arguments);
} // @needsAudit

/**
 * Unloads all the custom fonts. This is used for testing.
 */


function _loadFontInNamespaceAsync() {
  _loadFontInNamespaceAsync = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(fontFamily, source) {
    var asset;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            if (source) {
              _context3.next = 2;
              break;
            }

            throw new CodedError("ERR_FONT_SOURCE", "Cannot load null or undefined font source: { \"".concat(fontFamily, "\": ").concat(source, " }. Expected asset of type `FontSource` for fontFamily of name: \"").concat(fontFamily, "\""));

          case 2:
            if (!loaded[fontFamily]) {
              _context3.next = 4;
              break;
            }

            return _context3.abrupt("return");

          case 4:
            if (!loadPromises.hasOwnProperty(fontFamily)) {
              _context3.next = 6;
              break;
            }

            return _context3.abrupt("return", loadPromises[fontFamily]);

          case 6:
            // Important: we want all callers that concurrently try to load the same font to await the same
            // promise. If we're here, we haven't created the promise yet. To ensure we create only one
            // promise in the program, we need to create the promise synchronously without yielding the event
            // loop from this point.
            asset = getAssetForSource(source);
            loadPromises[fontFamily] = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
              return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                while (1) {
                  switch (_context2.prev = _context2.next) {
                    case 0:
                      _context2.prev = 0;
                      _context2.next = 3;
                      return loadSingleFontAsync(fontFamily, asset);

                    case 3:
                      loaded[fontFamily] = true;

                    case 4:
                      _context2.prev = 4;
                      delete loadPromises[fontFamily];
                      return _context2.finish(4);

                    case 7:
                    case "end":
                      return _context2.stop();
                  }
                }
              }, _callee2, null, [[0,, 4, 7]]);
            }))();
            _context3.next = 10;
            return loadPromises[fontFamily];

          case 10:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));
  return _loadFontInNamespaceAsync.apply(this, arguments);
}

export function unloadAllAsync() {
  return _unloadAllAsync.apply(this, arguments);
} // @needsAudit

/**
 * Unload custom fonts matching the `fontFamily`s and display values provided.
 * Because fonts are automatically unloaded on every platform this is mostly used for testing.
 *
 * @param fontFamilyOrFontMap The name or names of the custom fonts that will be unloaded.
 * @param options When `fontFamilyOrFontMap` is a string, this should be the font source used to load
 * the custom font originally.
 */

function _unloadAllAsync() {
  _unloadAllAsync = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
    var _i, _Object$keys, fontFamily;

    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            if (ExpoFontLoader.unloadAllAsync) {
              _context4.next = 2;
              break;
            }

            throw new UnavailabilityError('expo-font', 'unloadAllAsync');

          case 2:
            if (!Object.keys(loadPromises).length) {
              _context4.next = 4;
              break;
            }

            throw new CodedError("ERR_UNLOAD", "Cannot unload fonts while they're still loading: ".concat(Object.keys(loadPromises).join(', ')));

          case 4:
            for (_i = 0, _Object$keys = Object.keys(loaded); _i < _Object$keys.length; _i++) {
              fontFamily = _Object$keys[_i];
              delete loaded[fontFamily];
            }

            _context4.next = 7;
            return ExpoFontLoader.unloadAllAsync();

          case 7:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));
  return _unloadAllAsync.apply(this, arguments);
}

export function unloadAsync(_x5, _x6) {
  return _unloadAsync.apply(this, arguments);
}

function _unloadAsync() {
  _unloadAsync = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(fontFamilyOrFontMap, options) {
    var fontMap, names;
    return _regeneratorRuntime.wrap(function _callee5$(_context5) {
      while (1) {
        switch (_context5.prev = _context5.next) {
          case 0:
            if (ExpoFontLoader.unloadAsync) {
              _context5.next = 2;
              break;
            }

            throw new UnavailabilityError('expo-font', 'unloadAsync');

          case 2:
            if (!(typeof fontFamilyOrFontMap === 'object')) {
              _context5.next = 10;
              break;
            }

            if (!options) {
              _context5.next = 5;
              break;
            }

            throw new CodedError("ERR_FONT_API", "No fontFamily can be used for the provided options: ".concat(options, ". The second argument of `unloadAsync()` can only be used with a `string` value as the first argument."));

          case 5:
            fontMap = fontFamilyOrFontMap;
            names = Object.keys(fontMap);
            _context5.next = 9;
            return Promise.all(names.map(function (name) {
              return unloadFontInNamespaceAsync(name, fontMap[name]);
            }));

          case 9:
            return _context5.abrupt("return");

          case 10:
            _context5.next = 12;
            return unloadFontInNamespaceAsync(fontFamilyOrFontMap, options);

          case 12:
            return _context5.abrupt("return", _context5.sent);

          case 13:
          case "end":
            return _context5.stop();
        }
      }
    }, _callee5);
  }));
  return _unloadAsync.apply(this, arguments);
}

function unloadFontInNamespaceAsync(_x7, _x8) {
  return _unloadFontInNamespaceAsync.apply(this, arguments);
}

function _unloadFontInNamespaceAsync() {
  _unloadFontInNamespaceAsync = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6(fontFamily, options) {
    var nativeFontName;
    return _regeneratorRuntime.wrap(function _callee6$(_context6) {
      while (1) {
        switch (_context6.prev = _context6.next) {
          case 0:
            if (loaded[fontFamily]) {
              _context6.next = 4;
              break;
            }

            return _context6.abrupt("return");

          case 4:
            delete loaded[fontFamily];

          case 5:
            // Important: we want all callers that concurrently try to load the same font to await the same
            // promise. If we're here, we haven't created the promise yet. To ensure we create only one
            // promise in the program, we need to create the promise synchronously without yielding the event
            // loop from this point.
            nativeFontName = getNativeFontName(fontFamily);

            if (nativeFontName) {
              _context6.next = 8;
              break;
            }

            throw new CodedError("ERR_FONT_FAMILY", "Cannot unload an empty name");

          case 8:
            _context6.next = 10;
            return ExpoFontLoader.unloadAsync(nativeFontName, options);

          case 10:
          case "end":
            return _context6.stop();
        }
      }
    }, _callee6);
  }));
  return _unloadFontInNamespaceAsync.apply(this, arguments);
}

export { FontDisplay };